import React from 'react'
import classNames from 'classnames'
import { LayoutTemplate } from '../components/Layout'
import Section from '../components/common/Section'
import Container from '../components/common/Container'
import { Breadcrumb } from '../components/Breadcrumbs/Breadcrumbs'
import BreadcrumbsSEO from '../components/Breadcrumbs/BreadcrumbsSEO'
import { Content } from '../components/Content/Content'
import RelatedArticles from '../components/RelatedArticles/RelatedArticles'
import { PostProps } from '../components/Post/Post'
import AuthorComponent, { Author } from '../components/Author/Author'
import HeroImageComponent, { HeroImageComponentProps } from '../components/HeroImage/HeroImage'
import TagList from '../components/TagList/TagList'
import { Faq, FAQBlock } from '../components/widgets/FaqBlock/FaqBlock'
import ActionBar, { ActionBarProps } from '../components/ActionBar/ActionBar'
import TOC from '../components/widgets/TOC/TOCWrapper'
import BackButton from '../components/common/BackButton/BackButton'
import Button from '../components/common/Button'

import { displayDateFormatter } from '../helpers/format'
import { checkListLength } from '../helpers/blog'
import { PATH, URL } from '../constants/content'

import styles from './long-form-content.module.scss'

export interface TemplateProps {
  heroImage: HeroImageComponentProps
  post: PostProps
  body: string | React.ReactNode
  contentComponent?: React.FunctionComponent<{ content: string | React.ReactNode; className?: string }>
  breadcrumbs?: Array<Breadcrumb>
  authors?: Author[]
  publishDate?: string | null
  actionBar: ActionBarProps
  tags: string[]
  isHidden?: boolean | null
  isEditing?: boolean
  faqBlock?: Faq[]
  tocContent?: string
  cta?: {
    ctaTitle?: string | React.ReactNode
    showStartFreeTrial?: boolean
    showCtaSection?: boolean
  }
}

export default function LongFormContentTemplate({
  heroImage,
  post,
  body,
  contentComponent,
  breadcrumbs,
  authors = [],
  actionBar,
  tags,
  isHidden = false,
  isEditing = false,
  faqBlock,
  tocContent,
  cta,
}: TemplateProps) {
  const ContentComponent = contentComponent ?? Content
  const date = displayDateFormatter(new Date(actionBar?.publishDate))
  const content = tocContent ? checkListLength(tocContent) : null

  return (
    <>
      <LayoutTemplate isCmsPreview={isEditing}>
        {breadcrumbs && !isHidden && <BreadcrumbsSEO breadcrumbs={breadcrumbs} />}
        <Container size='large' className={styles.backContainer}>
          <BackButton href={PATH.blog} />
        </Container>
        <Section className={styles.root}>
          <Container className={styles.container}>
            <header className={styles.header}>
              <div className={styles.dateAndTags}>
                <h4>{date}</h4>
                {tags && <TagList tagLink='/blog/tag/' tags={tags} format='title' tagsLimit={3} />}
              </div>
              <h1 className={styles.title}>{post.title}</h1>
            </header>

            <aside className={styles.sideBar}>
              {authors && (
                <div className={styles.authors}>
                  {authors.map((author) => (
                    <AuthorComponent key={author.name} author={author} className={styles.author} />
                  ))}
                </div>
              )}

              {content && (
                <div
                  className={classNames(styles.toc, {
                    [styles.tocWithImage]: heroImage.image,
                    [styles.twoAuthors]: authors.length === 2,
                  })}
                >
                  <TOC content={content} />
                  {cta?.showCtaSection && (
                    <div className={styles.ctaItem}>
                      {cta?.ctaTitle}
                      <div className={styles.ctaButton}>
                        {cta?.showStartFreeTrial && (
                          <Button size='small' href={URL.signupUrl}>
                            Start Free Trial
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </aside>
            <article className={styles.body}>
              {heroImage.image && <HeroImageComponent {...heroImage} />}
              {content && (
                <div className={styles.mobileCard}>
                  <TOC content={content} />
                </div>
              )}
              <ContentComponent content={body} className={styles.content} />
              {tags && (
                <div className={styles.footerTags}>
                  <h3 className={styles.title}>All article tags</h3>
                  <TagList tagLink='/blog/tag/' tags={tags} format='title' />
                </div>
              )}
              {faqBlock && <FAQBlock faqBlocks={faqBlock} markdown={isEditing} />}
              <div className={styles.actionBar}>
                <ActionBar {...actionBar} showPublishedDate={false} showShareText />
              </div>
            </article>
          </Container>

          {!isEditing && (
            <Container size='large' className={styles.relatedArticles}>
              <RelatedArticles article={post} count={4} limitPostLines={true} outlined />
            </Container>
          )}
        </Section>
      </LayoutTemplate>
    </>
  )
}
